// src/App.jsx
import { useRoutes, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { CssBaseline } from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import ThemeProvider from './theme/ThemeProvider';
import router from './router';
import LoaderScreen from './LoaderScreen';
import ServerDownScreen from './ServerDownScreen';
import { socket } from './socket/config';
import { collabSocket } from './socket/collabSocket';
import { notificationsSocket } from './socket/notificationsSocket';
import { useNotificationAudio } from './utils/useNotificationAudio';
import { getProfile, logout } from './redux/authSlice';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './i18n';

function ProtectedRoute({ children }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((x) => x.user);
  const PUBLIC_ROUTES = ['/', '/404', '/forgetPassword', '/resetpassword'];
  const isPublicPage = PUBLIC_ROUTES.includes(location.pathname);

  // Fetch profile when authenticated (hook must be before any return)
  useEffect(() => {
    if (authUser.accessToken) {
      dispatch(getProfile());
    }
  }, [authUser.accessToken, dispatch]);

  // If not authenticated and not on a public page → redirect to login
  if (!authUser.accessToken && !isPublicPage) {
    window.location.href = process.env.REACT_APP_ADMIN_URL + '/login';
    return null;
  }

  // Check subscription validity once profile is loaded
  const profile = authUser.profile;
  const subscriptions = profile?.company?.subscriptions;

  if (authUser.accessToken && !isPublicPage && subscriptions) {
    // No subscriptions at all → redirect to guest app
    if (subscriptions.length === 0) {
      window.location.href = process.env.REACT_APP_ADMIN_URL;
      return null;
    }

    // Check the last (current) subscription
    const lastSub = subscriptions[subscriptions.length - 1];
    const hasActiveStatus =
      lastSub?.status === 'ACTIVE' || lastSub?.status === 'TRIAL';

    // Expired (or any non-active) status → block immediately
    if (!hasActiveStatus) {
      window.location.href = process.env.REACT_APP_ADMIN_URL;
      return null;
    }

    // Active/Trial status → block only if verification is REJECTED
    if (lastSub?.verificationStatus === 'REJECTED') {
      window.location.href = process.env.REACT_APP_ADMIN_URL;
      return null;
    }
  }

  // Issue 2: If the user is locked, block access
  if (authUser.accessToken && profile) {
    if (profile.locked === true || profile.locked === 'true') {
      // Force them back to login (or logout)
      dispatch(logout());
      return null;
    }

    // Issue 3: If user is specific role and hasn't changed password, force navigation to settings
    const FORCED_PASSWORD_ROLES = ['MANAGER', 'CLAIM_MANAGER', 'RESPONSIBLE'];
    if (
      profile.password_changed === false && 
      FORCED_PASSWORD_ROLES.includes(authUser.role) && 
      !isPublicPage
    ) {
      if (location.pathname !== '/management/profile/settings') {
        window.location.href = '/management/profile/settings';
        return null;
      }
    }
  }

  return children;
}

function App() {
  const content = useRoutes(router);
  const [isLoading, setIsLoading] = useState(false);
  const authUser = useSelector((x) => x.user);
  const serverDown = useSelector((x) => x.ui.serverDown);

  // ✅ Initialize audio unlock globally
  const { audioReady } = useNotificationAudio();

  // ✅ Log audio status
  useEffect(() => {
    if (audioReady) {
      console.log('🔊 Audio system ready');
    }
  }, [audioReady]);

  // ✅ Initialize sockets
  useEffect(() => {
    if (authUser.accessToken) {
      socket.connect();
      socket.on('connect', () => console.log('✅ Socket connected'));
      socket.on('disconnect', (reason) =>
        console.log('⚠️ Socket disconnected:', reason)
      );
      socket.on('connect_error', (err) =>
        console.error('❌ Socket error:', err)
      );

      const onCollabConnect = () => console.log('✅ CollabSocket connected');
      const onCollabDisconnect = (reason) => console.log('⚠️ CollabSocket disconnected:', reason);
      const onCollabError = (err) => console.error('❌ CollabSocket error:', err);

      collabSocket.connect();
      collabSocket.on('connect', onCollabConnect);
      collabSocket.on('disconnect', onCollabDisconnect);
      collabSocket.on('connect_error', onCollabError);

      const onNotifConnect = () => console.log('✅ NotificationsSocket connected');
      const onNotifDisconnect = (reason) => console.log('⚠️ NotificationsSocket disconnected:', reason);
      const onNotifError = (err) => console.error('❌ NotificationsSocket error:', err);

      notificationsSocket.connect();
      notificationsSocket.on('connect', onNotifConnect);
      notificationsSocket.on('disconnect', onNotifDisconnect);
      notificationsSocket.on('connect_error', onNotifError);

      return () => {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('connect_error');
        socket.disconnect();
        collabSocket.off('connect', onCollabConnect);
        collabSocket.off('disconnect', onCollabDisconnect);
        collabSocket.off('connect_error', onCollabError);
        collabSocket.disconnect();
        notificationsSocket.off('connect', onNotifConnect);
        notificationsSocket.off('disconnect', onNotifDisconnect);
        notificationsSocket.off('connect_error', onNotifError);
        notificationsSocket.disconnect();
      };
    }
  }, [authUser.accessToken]);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        {serverDown ? (
          <ServerDownScreen />
        ) : (
          <ProtectedRoute>
            {isLoading ? <LoaderScreen /> : content}
          </ProtectedRoute>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
